import React, { useRef, useState, useEffect } from 'react'
import { motion, useInView } from 'framer-motion'
import { getPageData, useData } from '../../app/helper/functions'

import images from '../../app/utlis/image'

import Heading from '../atoms/Heading/Heading'
import ReliabilityAIBusiness from '../molecules/AIBusinessComponents/ReliabilityAIBusiness/ReliabilityAIBusiness'
import AppsRunning from '../molecules/AppsSupportComponents/AppsRunning/AppsRunning'
import ProjectDisplay from '../molecules/ProjectDisplay/ProjectDisplay'
import AppsFaq from '../molecules/AppsFaq/AppsFaq'
import AIBusinessIdeaProject from '../molecules/AIBusinessComponents/AIBusinessIdeaProject/AIBusinessIdeaProject'
import DefaultFooter from '../organisms/DefaultFooter/DefaultFooter'

const AIBusinessPage = () => {
    const { data } = useData();
    const aiBusinessData = getPageData(data, 'aiBusinessPage');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Animation variants
    const fadeIn = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.8 } }
    }

    const slideUp = {
        hidden: { y: 50, opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
    }

    const slideIn = {
        hidden: { x: -50, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
    }

    // Create refs for each section
    const heroRef = useRef(null);
    const reliabilityRef = useRef(null);
    const ideaProjectRef = useRef(null);
    const appsRunningRef = useRef(null);
    const projectDisplayRef = useRef(null);
    const faqRef = useRef(null);

    // Use useInView hook for each section
    const heroInView = useInView(heroRef, { once: false, amount: 0.3 });
    const reliabilityInView = useInView(reliabilityRef, { once: false, amount: 0.3 });
    const ideaProjectInView = useInView(ideaProjectRef, { once: false, amount: 0.2 });
    const appsRunningInView = useInView(appsRunningRef, { once: false, amount: 0.2 });
    const projectDisplayInView = useInView(projectDisplayRef, { once: false, amount: 0.3 });
    const faqInView = useInView(faqRef, { once: false, amount: 0.3 });

    return (
        <div className='overflow-x-hidden'>
            <div className='bg-aiBusiness-bg bg-center bg-cover bg-no-repeat'>
                <div className='bg-blackTransparent'>
                    <div className='flex flex-col px-[5%] sm:px-[7%] md:px-[10%] pb-4 sm:pb-6 md:pb-8 pt-32 sm:pt-40 md:pt-52 items-center justify-center' ref={heroRef}>
                        <motion.img
                            src={images.AIHEALTHBUSINESSILL}
                            className='z-10 w-[90%] sm:w-[80%] md:w-[70%]'
                            initial={{ opacity: 0, y: -30 }}
                            animate={heroInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        />
                        <motion.div
                            className='text-white flex flex-col gap-1 sm:gap-2 relative text-center'
                            initial={{ opacity: 0 }}
                            animate={heroInView ? { opacity: 1 } : {}}
                            transition={{ duration: 0.8, delay: 0.3 }}
                        >
                            <motion.div
                                className='font-bold text-3xl sm:text-5xl md:text-7xl mb-1 sm:mb-2'
                                initial={{ opacity: 0, y: 20 }}
                                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.6, delay: 0.5 }}
                            >
                                {aiBusinessData.title}
                            </motion.div>
                            <motion.div
                                className='font-normal text-base sm:text-xl md:text-2xl'
                                initial={{ opacity: 0, y: 20 }}
                                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.6, delay: 0.7 }}
                            >
                                {aiBusinessData.description}
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </div>
            <div className='bg-blackDark'>
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={fadeIn}
                >
                    <Heading title='Benefits' className='bg-darkGreyPrimary py-6 sm:py-8 md:py-12'
                        subTitle='The advantages of AI for your business'
                        text="How do we confirm that AI integration enhances efficiency, lowers expenses, and promotes innovation? Here's the approach:" />
                </motion.div>
                <div ref={reliabilityRef}>
                    <motion.div
                        initial="hidden"
                        animate={reliabilityInView ? "visible" : "hidden"}
                        variants={slideUp}
                    >
                        <ReliabilityAIBusiness aiBusinessData={aiBusinessData} />
                    </motion.div>
                </div>
                <div ref={ideaProjectRef}>
                    <motion.div
                        initial="hidden"
                        animate={ideaProjectInView ? "visible" : "hidden"}
                        variants={slideUp}
                    >
                        <AIBusinessIdeaProject aiBusinessData={aiBusinessData} />
                    </motion.div>
                </div>
                <div ref={appsRunningRef} className='pt-5 sm:pt-7 md:pt-10 bg-blackDark'>
                    <motion.div
                        initial="hidden"
                        animate={appsRunningInView ? "visible" : "hidden"}
                        variants={slideIn}
                    >
                        <AppsRunning
                            bgImage='bg-confident-ai-bg'
                            title="Let's develop your AI solution!"
                            subtitle="Don't worry, you are in the right place."
                        />
                    </motion.div>
                </div>
                <div ref={projectDisplayRef}>
                    <motion.div
                        initial="hidden"
                        animate={projectDisplayInView ? "visible" : "hidden"}
                        variants={slideUp}
                    >
                        <ProjectDisplay />
                    </motion.div>
                </div>
                <div ref={faqRef}>
                    <motion.div
                        initial="hidden"
                        animate={faqInView ? "visible" : "hidden"}
                        variants={fadeIn}
                    >
                        <AppsFaq />
                    </motion.div>
                </div>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <DefaultFooter />
                </motion.div>
            </div>
        </div>
    )
}

export default AIBusinessPage
